import { Info } from "lucide-react";
import { cn } from "@/lib/cn";

type Accent = "blue" | "cyan" | "teal" | "green" | "amber" | "violet" | "red" | "indigo";

const ACCENT_BAR: Record<Accent, string> = {
  blue: "bg-accent-blue",
  cyan: "bg-accent-cyan",
  teal: "bg-accent-teal",
  green: "bg-accent-green",
  amber: "bg-accent-amber",
  violet: "bg-accent-violet",
  red: "bg-accent-red",
  indigo: "bg-indigo-500",
};

interface KpiCardProps {
  label: string;
  value: React.ReactNode;
  sublabel?: string;
  tooltip?: string;
  accent?: Accent;
  className?: string;
  onClick?: () => void;
}

export function KpiCard({ label, value, sublabel, tooltip, accent = "blue", className, onClick }: KpiCardProps) {
  return (
    // No overflow-hidden here — rounded-t-card on the bar handles the corners instead
    <div 
      className={cn(
        "relative rounded-card bg-card border border-border shadow-card px-5 py-4", 
        onClick && "cursor-pointer hover:bg-slate-50 transition-colors",
        className
      )}
      onClick={onClick}
    >
      <div className={cn("absolute inset-x-0 top-0 h-[3px] rounded-t-card", ACCENT_BAR[accent])} />
      <div className="flex items-center gap-1.5">
        <div className="label-xs">{label}</div>
        {tooltip && (
          <div className="relative group/tip flex items-center">
            <Info className="h-3 w-3 text-slate-400 cursor-default" />
            <div className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-52 rounded-md bg-white border border-border px-3 py-2 text-xs text-slate-700 shadow-md opacity-0 group-hover/tip:opacity-100 transition-opacity z-50">
              {tooltip}
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-white" />
            </div>
          </div>
        )}
      </div>
      <div className="num-display text-3xl sm:text-4xl mt-2.5 leading-none text-ink-900 numeric-value">
        {value}
      </div>
      {sublabel && (
        <div className="text-xs text-slate-500 mt-2.5">{sublabel}</div>
      )}
    </div>
  );
}
